import React, { useState, useCallback } from "react";
import Select, { SingleValue, StylesConfig, GroupBase, components } from 'react-select';
import "./pro-language-select.less";
import i18n from "../../../i18n";
import { JSX } from "react/jsx-runtime";

export interface ILangOptions {
    label: string;
    value: string;
}

export interface IProLanguageSelect extends React.PropsWithChildren {
    label: string;
    options: ILangOptions[];
    onSelect: (language: string) => void;
}

const selectStyles: StylesConfig<ILangOptions, false, GroupBase<ILangOptions>> = {
    control: (base, state) => ({
        ...base,
      
        '&:hover': { borderColor: 'solid 2px var(--bg-primary-color-default)',
                     border:'solid 2px var(--bg-primary-color-default)',
                     backgroundColor: 'var(--bg-primary-color-hover-default)',
                     color: 'black',
                    }, 
        border: state.isFocused ? '2px solid transparent': '2px solid transparent',
        outline: state.isFocused ? 'solid 4px var(--color-ltblue)': '0',
        color: "var(--bg-primary-color-default)",
        borderRadius: 10,
        backgroundColor: "trasparent",
        paddingLeft: 7,    
    }),
    menu:  (base) => ({
        ...base,
     
        width: "max-content",
        minWidth: "100%",
        fontSize: "1.125rem"
    }),
    option: (base, state) => ({
        ...base,
      }),
    singleValue: (base) => ({
        ...base,

        color: "var(--bg-primary-color-default)",
        fontSize: "1.125rem",
        '&:hover': { 
            backgroundColor: 'var(--bg-primary-color-hover-default)',
            color: 'black'

        },
    }),
    // --- KEY FOR DROPDOWN INDICATOR COLOR ---
    dropdownIndicator: (base, state) => ({
        ...base,

        color: 'var(--bg-primary-color-default)', 
        '&:hover': {
            color: 'black', // Make arrow black on hover
            cursor: 'pointer', // Indicate it's clickable
        },
    }),
    indicatorSeparator: (base) => ({
        ...base,
    
        backgroundColor: 'var(--bg-primary-color-default)', // Color of the separator line
        '&:hover': {
            color: 'black', 
            cursor: 'pointer', 
        },
    }),
    valueContainer: (base) => ({
        ...base,

        display: 'flex', // Override 'display' to 'flex'
        flexWrap: 'wrap', // Allow items to wrap if they exceed container width
        padding: "0px 8px 0px 8px"
    })
}
// Custom Option component to show checkmark in the dropdown list
const CustomOption = (props: any) => { // Use 'any' for props for simplicity with react-select's internal types
    return (
        <components.Option {...props}>
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                }}
            >
                <span lang={props.data.value}>{props.children}</span>  {/* The default label/content */}
                {props.isSelected && ( // Conditionally render checkmark if selected
                    <span
                        style={{
                            color: 'white',
                            marginLeft: 8,
                            fontSize: 18,
                            fontWeight: 'bolder',
                            lineHeight: 1,
                        }}
                    >
                        &#x2713; {/* Unicode checkmark */}
                    </span>
                )}
            </div>
        </components.Option>
    );
};


const CustomSingleValue = (props: {
        innerProps: JSX.IntrinsicAttributes & React.ClassAttributes<HTMLDivElement> & React.HTMLAttributes<HTMLDivElement>;
        data: { value: string | undefined; };
        children: React.ReactNode; // More general type for children
    }) => {
    const customStyles = {
        fontSize: "1.125rem",
    };

    return (
        <div {...props.innerProps} lang={props.data.value} style={customStyles}>
            {props.children}
        </div>
    );
};
const ProLanguageSelect = (props: IProLanguageSelect) => {
    const { onSelect, label, options } = props;
    const initialLanguage = options.find(lang => lang.value === i18n.language);
    const [selectedValue, setSelectedValue] = useState<SingleValue<ILangOptions>>(initialLanguage || options[0]);

    const handleChange = useCallback(
        (option: SingleValue<ILangOptions>) => {
            if (option) {
                setSelectedValue(option);
                onSelect(option.value);
                document.documentElement.lang = option.value;
            }
        },
        [onSelect]
    );

    const Dropdown = () => {
        return (
            <div className="pro-languageselect">
                <div id="languageselect-label" className="pro-languageselect-label">
                    {label}
                </div>
                <div className="pro-languageselect-select-wrapper">
                    <Select
                        name="languageselect"
                        id="languageselect"
                        className="pro-languageselect-select"
                        styles={selectStyles}
                        value={selectedValue}
                        onChange={handleChange}
                        isSearchable={false}
                        aria-labelledby="languageselect-label"
                        options={options}
                        components={{
                            SingleValue: CustomSingleValue, // Use custom SingleValue
                            Option: CustomOption // Use custom Option
                        }}  
                        theme={(theme) => ({
                            ...theme,
                            borderRadius: 0,
                            colors: {
                                ...theme.colors,
                                primary: "#5861B1",
                            },
                        })}    
                    />
                </div>
            </div>
        );
    };

    return (
        <div className="pro-languageselect-wrapper">
            <Dropdown />
        </div>
    );
};

export default ProLanguageSelect;
